import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, AlertTriangle, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = ({ inviteCode: propInviteCode }) => {
  const { login, register } = useAuth();
  const { settings, getLogoUrl } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    name: '', 
    email: '', 
    password: '', 
    phone: '',
    invite_code: '' 
  });

  // Auto-fill invite code from props or URL param
  useEffect(() => {
    const urlCode = searchParams.get('code');
    const codeToUse = propInviteCode || urlCode;
    
    if (codeToUse) {
      setRegisterData(prev => ({ ...prev, invite_code: codeToUse.toUpperCase() }));
      setActiveTab('register');
    }
  }, [propInviteCode, searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const hasInviteCode = propInviteCode || searchParams.get('code');
  const logoUrl = getLogoUrl();

  return (
    <div className="min-h-screen bg-void grid-pattern flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-sm bg-panel border border-white/10 overflow-hidden">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt={settings.app_title} 
                className="w-full h-full object-contain p-2"
              />
            ) : (
              <Shield className="w-10 h-10" style={{ color: settings.primary_color }} />
            )}
          </div>
          <div>
            <h1 className="font-chivo font-black text-3xl tracking-tight uppercase text-white">
              {settings.app_title}
            </h1>
            <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mt-1">
              {settings.app_subtitle}
            </p>
          </div>
        </div>

        {/* Invite Notice */}
        {hasInviteCode && (
          <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 flex items-center gap-3">
            <UserPlus className="w-5 h-5 text-primary shrink-0" />
            <div>
              <p className="font-inter text-sm text-white">You&apos;ve been invited to join the team!</p>
              <p className="font-mono text-xs text-muted-foreground">Complete registration below</p>
            </div>
          </div>
        )}

        {/* Auth Card */}
        <Card className="bg-panel border-white/10 rounded-sm" data-testid="auth-card">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-surface rounded-sm">
              <TabsTrigger 
                value="login" 
                className="font-mono text-xs uppercase tracking-wide rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="login-tab"
              >
                Login
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                className="font-mono text-xs uppercase tracking-wide rounded-sm data-[state=active]:bg-primary data-[state=active]:text-white"
                data-testid="register-tab"
              >
                Register
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin}>
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-xl text-white">Team Login</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Enter your credentials to access the system
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@truthchurch.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10"
                      data-testid="login-email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10"
                      data-testid="login-password"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wide rounded-sm h-10"
                    disabled={loading}
                    data-testid="login-submit"
                  >
                    {loading ? 'Authenticating...' : 'Access System'}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister}>
                <CardHeader>
                  <CardTitle className="font-chivo font-bold text-xl text-white">Join Team</CardTitle>
                  <CardDescription className="font-inter text-muted-foreground">
                    Use your invite code to register
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Invite Code
                    </Label>
                    <Input
                      placeholder="XXXXXXXX"
                      value={registerData.invite_code}
                      onChange={(e) => setRegisterData({ ...registerData, invite_code: e.target.value.toUpperCase() })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10 uppercase"
                      data-testid="register-invite"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Full Name
                    </Label>
                    <Input
                      placeholder="Your name"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10"
                      data-testid="register-name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Email
                    </Label>
                    <Input
                      type="email"
                      placeholder="you@truthchurch.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10"
                      data-testid="register-email"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Phone (Optional)
                    </Label>
                    <Input
                      type="tel"
                      placeholder="403-XXX-XXXX"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10"
                      data-testid="register-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
                      Password
                    </Label>
                    <Input
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className="bg-black border-white/20 rounded-sm font-mono text-sm h-10"
                      data-testid="register-password"
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-white text-black hover:bg-gray-200 font-bold uppercase tracking-wide rounded-sm h-10"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? 'Creating Account...' : 'Join Team'}
                  </Button>
                </CardContent>
              </form>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Security Notice */}
        <div className="flex items-center gap-2 justify-center text-muted-foreground">
          <AlertTriangle className="w-4 h-4" />
          <span className="font-mono text-xs">Authorized Personnel Only</span>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
